import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { digitalOcean } from "@/integrations/digitalocean/client";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { FileText } from "lucide-react";

export default function EULAAcceptance() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [hasAccepted, setHasAccepted] = useState(false);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    const { data: { session } } = await digitalOcean.auth.getSession();
    if (!session) {
      navigate("/Auth");
      return;
    }
    const { data: profile } = await digitalOcean
      .from("profiles")
      .select("*")
      .eq("id", session.user.id)
      .single();
    setCurrentUser(profile);
    
    if (profile.has_accepted_eula) {
      navigate("/Discover");
    }
  };

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const { error } = await digitalOcean
        .from("profiles")
        .update({ has_accepted_eula: true })
        .eq("id", currentUser.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("EULA accepted");
      navigate("/Discover");
    },
    onError: () => {
      toast.error("Failed to accept EULA");
    },
  });

  const handleAccept = () => {
    if (!hasAccepted) {
      toast.error("Please check the box to accept the EULA");
      return;
    }
    acceptMutation.mutate();
  };

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8 flex items-center justify-center">
      <div className="max-w-4xl w-full bg-card p-8 rounded-lg border border-border">
        <div className="flex items-center gap-3 mb-6">
          <FileText className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold text-card-foreground">End User License Agreement</h1>
        </div>

        <div className="bg-muted/50 p-6 rounded max-h-96 overflow-y-auto mb-6">
          <p className="text-sm text-foreground mb-4">
            Last Updated: {new Date().toLocaleDateString()}
          </p>
          
          <h2 className="text-lg font-semibold text-foreground mb-2">1. Acceptance of Terms</h2>
          <p className="text-muted-foreground mb-4">
            By accessing and using this platform, you accept and agree to be bound by the terms and provisions of this agreement.
          </p>

          <h2 className="text-lg font-semibold text-foreground mb-2">2. Use License</h2>
          <p className="text-muted-foreground mb-4">
            Permission is granted to use the platform for personal, non-commercial purposes subject to the restrictions in this agreement.
          </p>

          <h2 className="text-lg font-semibold text-foreground mb-2">3. User Content</h2>
          <p className="text-muted-foreground mb-4">
            You retain all rights to content you upload. By uploading, you grant us a license to host and display your content on the platform.
          </p>

          <h2 className="text-lg font-semibold text-foreground mb-2">4. Prohibited Uses</h2>
          <p className="text-muted-foreground mb-4">
            You may not use the platform for any illegal purposes or to violate any laws, upload malicious content, or harass other users.
          </p>

          <h2 className="text-lg font-semibold text-foreground mb-2">5. Termination</h2>
          <p className="text-muted-foreground mb-4">
            We may terminate or suspend access to the platform immediately, without prior notice, for any breach of these terms.
          </p>
        </div>

        <div className="flex items-center space-x-2 mb-6">
          <Checkbox 
            id="accept" 
            checked={hasAccepted}
            onCheckedChange={setHasAccepted}
          />
          <label
            htmlFor="accept"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            I have read and accept the End User License Agreement
          </label>
        </div>

        <div className="flex gap-4">
          <Button 
            onClick={handleAccept} 
            className="flex-1"
            disabled={!hasAccepted || acceptMutation.isPending}
            tooltip={!hasAccepted ? "Please check the box to accept" : acceptMutation.isPending ? "Accepting EULA..." : "Accept EULA and continue"}
          >
            {acceptMutation.isPending ? "Accepting..." : "Accept and Continue"}
          </Button>
          <Button variant="outline" onClick={() => navigate("/EULA")} tooltip="View the full End User License Agreement document">
            Read Full EULA
          </Button>
        </div>
      </div>
    </div>
  );
}
